// AdminLearningPage.jsx — admin L&D dashboard with 6 tabs.
import React, { useCallback, useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import {
  LayoutDashboard, BookOpen, Route, Shield, Sparkles, ClipboardList,
  Plus, X, Check,
} from "lucide-react";
import { toast } from "../../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON = "#00FF99";
const AMBER = "#EF9F27";
const RED = "#E24B4A";
const BLUE = "#378ADD";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const TABS = [
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { key: "courses",   label: "Courses",   Icon: BookOpen },
  { key: "paths",     label: "Paths",     Icon: Route },
  { key: "compliance",label: "Compliance",Icon: Shield },
  { key: "skills",    label: "Skills",    Icon: Sparkles },
  { key: "requests",  label: "Requests",  Icon: ClipboardList },
];

export default function AdminLearningPage({ embedded = false }) {
  const [tab, setTab] = useState("dashboard");

  const inner = (
    <div className={embedded ? "" : "p-6 space-y-5 text-white"}>
      {!embedded && (
        <h1 style={{ fontSize: 18, fontWeight: 500, color: "#fff", margin: "0 0 14px 0" }}>
          Learning Administration
        </h1>
      )}
      <div style={{ display: "flex", gap: 4, borderBottom: "0.5px solid #1c1c1c", marginBottom: 16, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={tabStyle(tab === t.key)}>
            <t.Icon size={12} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "dashboard"  && <DashboardTab />}
      {tab === "courses"    && <CoursesTab />}
      {tab === "paths"      && <PathsTab />}
      {tab === "compliance" && <ComplianceTab />}
      {tab === "skills"     && <SkillsTab />}
      {tab === "requests"   && <RequestsTab />}
    </div>
  );
  return embedded ? inner : <MessengerLayout>{inner}</MessengerLayout>;
}

// ── DASHBOARD ─────────────────────────────────────────────────────────
function DashboardTab() {
  const [k, setK] = useState(null);
  const [byCourse, setByCourse] = useState([]);
  const [compliance, setCompliance] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/learning/analytics/overview`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null).then(setK);
    fetch(`${API_BASE}/api/learning/analytics/courses`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : []).then(setByCourse);
    fetch(`${API_BASE}/api/learning/compliance/summary`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null).then(setCompliance);
  }, []);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
        <Stat label="Total courses"      value={k?.total_courses ?? "—"} color={BLUE} />
        <Stat label="Active learners"    value={k?.active_learners ?? "—"} color={NEON} />
        <Stat label="Completion rate"    value={k ? `${k.completion_rate}%` : "—"} color={NEON} />
        <Stat label="Compliance rate"    value={k ? `${k.compliance_rate}%` : "—"} color={AMBER} />
        <Stat label="Certs expiring 30d" value={k?.certs_expiring_30d ?? "—"} color={AMBER} />
        <Stat label="Pending requests"   value={k?.pending_training_requests ?? "—"} color={BLUE} />
      </div>
      <div style={card}>
        <h3 style={section}>Course completion (bar chart)</h3>
        <div style={{ display: "grid", gap: 6 }}>
          {byCourse.map((c) => (
            <div key={c.course_id} style={{ display: "grid", gridTemplateColumns: "1fr 200px 60px", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#ddd", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.title}</span>
              <div style={{ height: 8, background: "#1a1a1a", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${c.completion_rate}%`, background: NEON, height: "100%" }} />
              </div>
              <span style={{ fontSize: 11, color: "#888", textAlign: "right" }}>{c.completion_rate}% ({c.completions}/{c.enrolments})</span>
            </div>
          ))}
        </div>
      </div>
      {compliance && compliance.by_department.length > 0 && (
        <div style={card}>
          <h3 style={section}>Compliance by department</h3>
          <div style={{ display: "grid", gap: 6 }}>
            {compliance.by_department.map((d) => (
              <div key={d.department} style={{ display: "grid", gridTemplateColumns: "120px 1fr 60px", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "#ddd" }}>{d.department}</span>
                <div style={{ height: 8, background: "#1a1a1a", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    width: `${d.rate}%`, height: "100%",
                    background: d.rate >= 80 ? NEON : d.rate >= 50 ? AMBER : RED,
                  }} />
                </div>
                <span style={{ fontSize: 11, color: "#888", textAlign: "right" }}>{d.rate}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── COURSES ───────────────────────────────────────────────────────────
function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/learning/org-courses`, { headers: authHeaders() });
    if (r.ok) setCourses(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: "#666" }}>{courses.length} courses</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowCreate(true)} style={btnPrimary}>
          <Plus size={12} /> Create course
        </button>
      </div>
      <div style={card}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Title", "Mandatory", "Enrolments", "Completed", "Rate"].map((h) =>
              <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.course_id}>
                <td style={td}>{c.title}</td>
                <td style={td}>{c.is_mandatory ? <span style={badge(RED)}>Mandatory</span> : "—"}</td>
                <td style={td}>{c.enrolments}</td>
                <td style={td}>{c.completed}</td>
                <td style={td}>
                  <span style={{ color: c.completion_rate >= 80 ? NEON : c.completion_rate >= 50 ? AMBER : RED }}>
                    {c.completion_rate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCreate && <CreateCourseModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateCourseModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    title: "", description: "", difficulty: "beginner",
    duration_minutes: 30, pass_score: 80, max_attempts: 3,
    is_mandatory: false, status: "draft",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Content-item builder state
  const [uploadProgress, setUploadProgress] = useState({});  // {filename: pct}
  const [uploadedItems,  setUploadedItems]  = useState([]);  // [{title, content_type, content_url, content_text?, duration_minutes}]
  const [addingContent,  setAddingContent]  = useState(null); // "link" | "text" | null
  const [linkForm, setLinkForm] = useState({ title: "", url: "", description: "", duration: 0 });
  const [textForm, setTextForm] = useState({ title: "", body: "", duration: 0 });

  const removeItem = (i) =>
    setUploadedItems((prev) => prev.filter((_, j) => j !== i));

  const uploadWithProgress = (file, type) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const fd  = new FormData();
      fd.append("file", file);
      fd.append("content_type", type);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress((p) => ({
            ...p, [file.name]: Math.round((e.loaded / e.total) * 100),
          }));
        }
      };
      xhr.onload = () => {
        setUploadProgress((p) => { const n = { ...p }; delete n[file.name]; return n; });
        if (xhr.status === 200) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (e) { reject(new Error("Bad response")); }
        } else {
          reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => {
        setUploadProgress((p) => { const n = { ...p }; delete n[file.name]; return n; });
        reject(new Error("Network error"));
      };
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      xhr.open("POST", `${API_BASE}/api/learning/upload`);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.send(fd);
    });

  const handleFile = async (file, type) => {
    if (!file) return;
    try {
      const res = await uploadWithProgress(file, type);
      setUploadedItems((prev) => [...prev, {
        title:            file.name.replace(/\.[^.]+$/, ""),
        content_type:     type,
        content_url:      res.url,
        duration_minutes: 0,
      }]);
    } catch (e) {
      toast.error(`${type === "video" ? "Video" : "Document"} upload failed: ${e.message}`);
    }
  };

  const addLinkItem = () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) return;
    setUploadedItems((prev) => [...prev, {
      title:            linkForm.title.trim(),
      content_type:     "link",
      content_url:      linkForm.url.trim(),
      content_text:     linkForm.description || null,
      duration_minutes: +linkForm.duration || 0,
    }]);
    setLinkForm({ title: "", url: "", description: "", duration: 0 });
    setAddingContent(null);
  };
  const addTextItem = () => {
    if (!textForm.title.trim() || !textForm.body.trim()) return;
    setUploadedItems((prev) => [...prev, {
      title:            textForm.title.trim(),
      content_type:     "text",
      content_text:     textForm.body.trim(),
      duration_minutes: +textForm.duration || 0,
    }]);
    setTextForm({ title: "", body: "", duration: 0 });
    setAddingContent(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // 1) Create the course
      const r = await fetch(`${API_BASE}/api/learning/courses`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) { setErr(await r.text()); setBusy(false); return; }
      const { id: newCourseId } = await r.json();

      // 2) Post each content item to the course
      for (let i = 0; i < uploadedItems.length; i++) {
        const item = uploadedItems[i];
        await fetch(`${API_BASE}/api/learning/courses/${newCourseId}/content`, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ ...item, order_index: i }),
        });
      }
      onDone();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div style={modalBg} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ ...modalCard, width: 540 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <strong style={{ color: "#fff", fontSize: 14 }}>Create course</strong>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={iconBtn}><X size={14} /></button>
        </div>
        <Field label="Title">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={inp} />
        </Field>
        <Field label="Description">
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="Difficulty">
            <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} style={inp}>
              <option>beginner</option><option>intermediate</option><option>advanced</option>
            </select>
          </Field>
          <Field label="Duration (min)">
            <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: +e.target.value })} style={inp} />
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="Pass score (%)">
            <input type="number" value={form.pass_score} onChange={(e) => setForm({ ...form, pass_score: +e.target.value })} style={inp} />
          </Field>
          <Field label="Max attempts">
            <input type="number" value={form.max_attempts} onChange={(e) => setForm({ ...form, max_attempts: +e.target.value })} style={inp} />
          </Field>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#bbb", margin: "8px 0" }}>
          <input type="checkbox" checked={form.is_mandatory} onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked })} />
          Mandatory
        </label>
        <Field label="Status">
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={inp}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </Field>

        {/* ── Content items builder ─────────────────────────────────── */}
        <div style={{
          marginTop: 14, paddingTop: 14, borderTop: "0.5px solid #1c1c1c",
        }}>
          <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Content items
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <button type="button" onClick={() => setAddingContent("link")}  style={contentBtn}>+ Link</button>
            <button type="button" onClick={() => document.getElementById("admin-video-upload").click()} style={contentBtn}>+ Video</button>
            <button type="button" onClick={() => document.getElementById("admin-doc-upload").click()}   style={contentBtn}>+ Document</button>
            <button type="button" onClick={() => setAddingContent("text")}  style={contentBtn}>+ Text</button>
          </div>

          {/* Hidden file inputs */}
          <input
            type="file" id="admin-video-upload" accept="video/*" style={{ display: "none" }}
            onChange={(e) => { handleFile(e.target.files[0], "video"); e.target.value = ""; }}
          />
          <input
            type="file" id="admin-doc-upload" accept=".pdf,.docx,.doc,.pptx,.xlsx,.txt" style={{ display: "none" }}
            onChange={(e) => { handleFile(e.target.files[0], "document"); e.target.value = ""; }}
          />

          {/* Inline link form */}
          {addingContent === "link" && (
            <div style={{ background: "#0a0a0a", border: "0.5px solid #2a2a2a", borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <input value={linkForm.title} onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })} placeholder="Lesson title" style={{ ...inp, marginBottom: 6 }} />
              <input value={linkForm.url}   onChange={(e) => setLinkForm({ ...linkForm, url:   e.target.value })} placeholder="https://…" style={{ ...inp, marginBottom: 6 }} />
              <input value={linkForm.description} onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })} placeholder="Description (optional)" style={{ ...inp, marginBottom: 6 }} />
              <input type="number" value={linkForm.duration} onChange={(e) => setLinkForm({ ...linkForm, duration: +e.target.value })} placeholder="Duration (min)" style={{ ...inp, marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" onClick={addLinkItem} style={btnPrimary}>Add</button>
                <button type="button" onClick={() => setAddingContent(null)} style={contentBtn}>Cancel</button>
              </div>
            </div>
          )}

          {/* Inline text form */}
          {addingContent === "text" && (
            <div style={{ background: "#0a0a0a", border: "0.5px solid #2a2a2a", borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <input value={textForm.title} onChange={(e) => setTextForm({ ...textForm, title: e.target.value })} placeholder="Lesson title" style={{ ...inp, marginBottom: 6 }} />
              <textarea rows={4} value={textForm.body} onChange={(e) => setTextForm({ ...textForm, body: e.target.value })} placeholder="Body…" style={{ ...inp, marginBottom: 6, fontFamily: "inherit", resize: "vertical" }} />
              <input type="number" value={textForm.duration} onChange={(e) => setTextForm({ ...textForm, duration: +e.target.value })} placeholder="Duration (min)" style={{ ...inp, marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" onClick={addTextItem} style={btnPrimary}>Add</button>
                <button type="button" onClick={() => setAddingContent(null)} style={contentBtn}>Cancel</button>
              </div>
            </div>
          )}

          {/* Active uploads */}
          {Object.entries(uploadProgress).map(([name, pct]) => (
            <div key={name} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                <span>{name}</span><span>{pct}%</span>
              </div>
              <div style={{ height: 4, background: "#1a1a1a", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: NEON, transition: "width 0.3s", borderRadius: 2 }} />
              </div>
            </div>
          ))}

          {/* Uploaded item list */}
          {uploadedItems.map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", background: "#0f0f0f",
              border: "0.5px solid #1c1c1c", borderRadius: 8, marginBottom: 6,
            }}>
              <span style={{ fontSize: 16 }}>
                {item.content_type === "video" ? "📹" :
                 item.content_type === "document" ? "📄" :
                 item.content_type === "link" ? "🔗" : "📝"}
              </span>
              <span style={{ flex: 1, fontSize: 12, color: "#ccc",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.title}
              </span>
              <span style={{ fontSize: 10, color: "#555" }}>{item.content_type}</span>
              <button type="button" onClick={() => removeItem(i)} style={{
                background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 14,
              }}>×</button>
            </div>
          ))}
        </div>

        {err && <p style={{ color: RED, fontSize: 11 }}>⚠ {err}</p>}
        <button type="submit" disabled={busy} style={{ ...btnPrimary, marginTop: 10 }}>
          {busy ? "Saving…" : "Create course"}
        </button>
      </form>
    </div>
  );
}

const contentBtn = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", fontSize: 11, borderRadius: 8,
  background: "transparent", border: "0.5px solid #2a2a2a",
  color: "#ccc", cursor: "pointer",
};

// ── PATHS ─────────────────────────────────────────────────────────────
function PathsTab() {
  const [paths, setPaths] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE}/api/learning/paths`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : []).then(setPaths);
  }, []);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: 10 }}>
      {paths.map((p) => (
        <div key={p.id} style={card}>
          <div style={{ height: 4, background: NEON, borderRadius: 2, marginBottom: 8 }} />
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{p.title}</div>
          <p style={{ fontSize: 11, color: "#888", margin: "4px 0 8px 0" }}>{p.description}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {(p.target_roles || []).map((r) => <span key={r} style={badge(BLUE)}>{r}</span>)}
            {(p.target_depts || []).map((d) => <span key={d} style={badge(AMBER)}>{d}</span>)}
            {p.auto_assign && <span style={badge(NEON)}>Auto-assign</span>}
          </div>
          <div style={{ fontSize: 10, color: "#666", marginTop: 8 }}>{p.estimated_hours}h estimated</div>
        </div>
      ))}
      {paths.length === 0 && <p style={{ color: "#666" }}>No learning paths yet.</p>}
    </div>
  );
}

// ── COMPLIANCE ────────────────────────────────────────────────────────
function ComplianceTab() {
  const [m, setM] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/learning/compliance/matrix`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null).then(setM);
  }, []);
  if (!m) return <p style={{ color: "#666" }}>Loading…</p>;
  const cellColor = (st) => st === "compliant" ? NEON : st === "expiring_soon" ? AMBER : RED;
  const cellSym = (st) => st === "compliant" ? "✓" : st === "expiring_soon" ? "⚠" : "✗";
  return (
    <div style={card}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Employee</th><th style={th}>Dept</th>
              {m.courses.map((c) => <th key={c.id} style={{ ...th, fontSize: 9 }}>{c.title}</th>)}
            </tr>
          </thead>
          <tbody>
            {m.rows.map((r) => (
              <tr key={r.user_id}>
                <td style={td}>{r.user_name}</td>
                <td style={td}>{r.department || "—"}</td>
                {r.cells.map((c, i) => (
                  <td key={i} style={{ ...td, textAlign: "center" }}>
                    <span style={{ color: cellColor(c.status), fontWeight: 600 }}>{cellSym(c.status)}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SKILLS ────────────────────────────────────────────────────────────
function SkillsTab() {
  const [skills, setSkills] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [newName, setNewName] = useState("");
  const load = useCallback(async () => {
    const a = await fetch(`${API_BASE}/api/learning/skills`, { headers: authHeaders() });
    if (a.ok) setSkills(await a.json());
    const g = await fetch(`${API_BASE}/api/learning/skills/gaps`, { headers: authHeaders() });
    if (g.ok) setGaps(await g.json());
  }, []);
  useEffect(() => { load(); }, [load]);
  const addSkill = async () => {
    if (!newName.trim()) return;
    await fetch(`${API_BASE}/api/learning/skills`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    });
    setNewName(""); load();
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <div style={card}>
        <h3 style={section}>Skills framework ({skills.length})</h3>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New skill name" style={inp} />
          <button onClick={addSkill} style={btnPrimary}><Plus size={12} /> Add</button>
        </div>
        <div style={{ display: "grid", gap: 4, maxHeight: 360, overflowY: "auto" }}>
          {skills.map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", fontSize: 11, color: "#ddd" }}>
              <span>{s.name}</span>
              <span style={{ color: "#666" }}>{s.category || "—"}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={card}>
        <h3 style={section}>Top skill gaps</h3>
        <div style={{ display: "grid", gap: 6 }}>
          {gaps.slice(0, 8).map((g) => (
            <div key={g.skill_id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 60px", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#ddd" }}>{g.skill_name}</span>
              <div style={{ height: 6, background: "#1a1a1a", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${g.gap_pct}%`, height: "100%",
                              background: g.gap_pct >= 70 ? RED : g.gap_pct >= 40 ? AMBER : NEON }} />
              </div>
              <span style={{ fontSize: 11, color: "#666", textAlign: "right" }}>{g.gap_pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── REQUESTS ──────────────────────────────────────────────────────────
function RequestsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/learning/requests`, { headers: authHeaders() });
    if (r.ok) setRows(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, status) => {
    const notes = window.prompt("Notes for this decision:") || "";
    await fetch(`${API_BASE}/api/learning/requests/${id}`, {
      method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ status, notes }),
    });
    load();
  };

  const visible = rows.filter((r) => r.status === filter);
  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} style={tabStyle(filter === s)}>
            {s} ({rows.filter((r) => r.status === s).length})
          </button>
        ))}
      </div>
      <div style={card}>
        {visible.length === 0 ? <p style={{ color: "#666", fontSize: 12, padding: 14 }}>No {filter} requests.</p> :
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Requester", "Course", "Provider", "Cost", "Justification", ""].map((h) =>
              <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id}>
                <td style={td}>{r.requested_by_name || "?"}</td>
                <td style={td}>{r.course_title}</td>
                <td style={td}>{r.provider || "—"}</td>
                <td style={td}>{r.cost != null ? `${r.cost} ${r.currency}` : "—"}</td>
                <td style={{ ...td, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>{r.justification}</td>
                <td style={td}>
                  {filter === "pending" && (
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => decide(r.id, "approved")} style={{ ...btnPrimary, padding: "4px 8px" }}><Check size={11} /></button>
                      <button onClick={() => decide(r.id, "rejected")} style={{ ...btnPrimary, padding: "4px 8px", background: "rgba(226,75,74,0.12)", borderColor: `${RED}55`, color: RED }}><X size={11} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────────────
function Stat({ label, value, color }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, color, fontWeight: 600, marginTop: 4, fontFamily: "ui-monospace, monospace" }}>{value}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

const card = { background: "#0d0d0d", border: "0.5px solid #1c1c1c", borderRadius: 10, padding: 14 };
const section = { fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 10px 0" };
const th = { padding: "8px 10px", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "0.5px solid #1c1c1c", textAlign: "left" };
const td = { padding: "8px 10px", fontSize: 11, color: "#ddd", borderBottom: "0.5px solid #131313" };
const tabStyle = (active) => ({
  padding: "8px 14px", fontSize: 12, background: "transparent", border: "none", cursor: "pointer",
  borderBottom: active ? `2px solid ${NEON}` : "2px solid transparent",
  color: active ? NEON : "#888", marginBottom: -1,
  display: "inline-flex", alignItems: "center", gap: 6, textTransform: "capitalize",
});
const badge = (color) => ({
  display: "inline-flex", alignItems: "center", gap: 3,
  padding: "1px 8px", borderRadius: 4, fontSize: 9,
  background: `${color}1a`, color, fontWeight: 500,
});
const inp = {
  background: "#0a0a0a", border: "0.5px solid #2a2a2a", borderRadius: 8,
  padding: "6px 10px", fontSize: 12, color: "#fff", outline: "none", width: "100%",
};
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "7px 14px", fontSize: 11, borderRadius: 8,
  background: `${NEON}1f`, border: `0.5px solid ${NEON}55`,
  color: NEON, cursor: "pointer", fontWeight: 500,
};
const iconBtn = {
  background: "transparent", border: "0.5px solid #2a2a2a",
  borderRadius: 8, padding: 4, color: "#888", cursor: "pointer",
};
const modalBg = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
  backdropFilter: "blur(4px)", zIndex: 200,
  display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
};
const modalCard = {
  background: "#111", border: "0.5px solid #1c1c1c", borderRadius: 12,
  padding: 18, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto",
};
